// paymentPlanClient — AUTHENTICATED staff client for the standalone Security
// Deposit Payment Plan agreement (WEL-77).
//
// One authed action: initiate the agreement on a current occupancy. Staff have
// already picked property → unit → current residents and entered the plan
// numbers; this POSTs the lot and the backend builds the DocuSeal roster
// (residents sign, then Miles countersigns) and sends it for signature.
//
// The feature is DORMANT by default behind PAYMENT_PLAN_SEND_ENABLED (+
// WHP_OWN_MESSAGING) — it's a legal document, so until Miles flips the flag the
// initiate route answers 503. We surface that as `PaymentPlanNotEnabledError`
// so the page can render a friendly "not turned on yet" state instead of a raw
// error toast (mirrors lib/depositAgreementClient.ts's 503 handling).
//
// Wire shapes are written against the backend contract in
// apps/api/src/lib/paymentPlanDoc.ts + the initiate route the backend agent is
// building. Reconciled at integration — see the report for the exact response
// shape assumed.

import { api, ApiError } from './api';
import type { PaymentPlanPrincipalType } from './paymentPlanForm';

// Re-export the dependency-free form helpers/types so app call sites can import
// everything from this one client module (the helpers live in paymentPlanForm
// so they stay unit-testable without pulling in the network `api` chain).
export {
  PAYMENT_PLAN_MIN_PAYMENTS,
  PAYMENT_PLAN_MAX_PAYMENTS,
  PAYMENT_PLAN_DEFAULT_INTERVAL_MONTHS,
  PAYMENT_PLAN_MAX_SPAN_MONTHS,
  computePaymentScheduleCents,
  paymentPlanSpanMonths,
  validatePaymentPlanInput,
  isResidentRowValid,
  isResidentEmailInvalid,
  canSendPaymentPlan,
} from './paymentPlanForm';
export type {
  ScheduledPayment,
  ComputeScheduleArgs,
  PaymentPlanInput,
  PaymentPlanPrincipalType,
  PaymentPlanResidentDraft,
  PaymentPlanFormDraft,
} from './paymentPlanForm';

/** One resident on the agreement, as the initiate route expects it. */
export interface PaymentPlanInitiateResident {
  tenantId: string;
  firstName: string;
  lastName: string;
  email: string;
}

/** Body for POST /api/payment-plan/initiate. */
export interface InitiatePaymentPlanBody {
  propertyId: string;
  unitId: string;
  residents: PaymentPlanInitiateResident[];
  /** Principal financed, in integer CENTS. */
  principalCents: number;
  /** Number of equal monthly payments (2–6). */
  numberOfPayments: number;
  /** First charge date, yyyy-mm-dd. */
  firstChargeDate: string;
  /**
   * TYPE-ADAPTIVE principal — what the plan finances. The backend defaults this
   * to 'security_deposit' when omitted.
   */
  principalType?: PaymentPlanPrincipalType;
  /** Freeform label sent ONLY when principalType is 'other'. */
  otherLabel?: string;
  /**
   * The occupancy's lease start date (yyyy-mm-dd) — the "lease dated ___" in the
   * agreement. REQUIRED: the backend rejects with `lease_start_date_required`
   * when it (and the mirror) are both empty. Staff prefill it from the mirror
   * value and may override it.
   */
  leaseStartDate?: string;
}

/** Backend response from POST /api/payment-plan/initiate. */
export interface InitiatePaymentPlanResult {
  groupId: string;
  /** How many signing requests went out. */
  sent: number;
  /** The roster the agreement went to (residents + the Lessor countersigner). */
  recipients: Array<{ role: string; name: string; hasEmail: boolean }>;
}

/**
 * Thrown when the initiate route answers 503 — the feature is gated off
 * (PAYMENT_PLAN_SEND_ENABLED / WHP_OWN_MESSAGING). The page catches this to
 * render the friendly "not enabled yet" state rather than an error toast.
 */
export class PaymentPlanNotEnabledError extends Error {
  constructor(message = 'Payment plan sending is not enabled yet.') {
    super(message);
    this.name = 'PaymentPlanNotEnabledError';
  }
}

/**
 * Initiate the standalone payment-plan agreement on a current occupancy: render
 * it from the plan numbers + roster and send it for signature.
 *
 * Throws PaymentPlanNotEnabledError on a 503 (feature dormant); other errors
 * propagate as ApiError for the caller's catch.
 */
export async function initiatePaymentPlan(
  body: InitiatePaymentPlanBody,
): Promise<InitiatePaymentPlanResult> {
  try {
    const raw = await api.post<{
      groupId: string;
      sent?: number;
      recipients?: Array<{ role: string; name: string; hasEmail: boolean }>;
    }>('/api/payment-plan/initiate', body);
    return {
      groupId: raw.groupId,
      sent: raw.sent ?? 0,
      recipients: raw.recipients ?? [],
    };
  } catch (err) {
    if (err instanceof ApiError && err.status === 503) {
      throw new PaymentPlanNotEnabledError(
        ((err.body as { error?: string } | null)?.error) ??
          'Payment plan sending is not enabled yet.',
      );
    }
    throw err;
  }
}
